import React, { useCallback, useEffect, useMemo, useState } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import {
  fetchOwnerPayoutScreenData,
  submitOwnerPayoutAccount,
} from "../api/ownerPayoutApi";

const STATUS_META = {
  PENDING: { label: "Pending", tone: "slate" },
  QUEUED: { label: "Queued", tone: "amber" },
  PROCESSING: { label: "Processing", tone: "blue" },
  SUCCESS: { label: "Paid", tone: "emerald" },
  PAID: { label: "Paid", tone: "emerald" },
  HOLD: { label: "Hold", tone: "rose" },
  FAILED: { label: "Failed", tone: "rose" },
  DRAFT: { label: "Draft", tone: "slate" },
  SUBMITTED: { label: "Submitted", tone: "amber" },
  ACTION_REQUIRED: { label: "Action required", tone: "rose" },
  VERIFIED: { label: "Verified", tone: "emerald" },
};

const toneClass = {
  slate: "border-slate-200 bg-slate-50 text-slate-700",
  amber: "border-amber-200 bg-amber-50 text-amber-700",
  blue: "border-sky-200 bg-sky-50 text-sky-700",
  emerald: "border-emerald-200 bg-emerald-50 text-emerald-700",
  rose: "border-rose-200 bg-rose-50 text-rose-700",
};

const PERIODS = [
  { value: "all", label: "All time" },
  { value: "7d", label: "Last 7 days" },
  { value: "30d", label: "Last 30 days" },
  { value: "90d", label: "Last 90 days" },
];

function money(value) {
  const num = Number(value || 0);
  return `₹${Number.isFinite(num) ? num.toLocaleString("en-IN", { maximumFractionDigits: 2 }) : "0"}`;
}

function toIsoDate(value) {
  if (!value) return null;
  const dt = new Date(value);
  return Number.isNaN(dt.getTime()) ? null : dt.toISOString();
}

function formatDateTime(value) {
  const iso = toIsoDate(value);
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function normalizeBookingStatus(raw) {
  const s = String(raw || "").trim().toUpperCase();
  if (!s) return "PENDING";
  if (s === "SUCCESS") return "PAID";
  return s;
}

function statusInfo(status) {
  const key = normalizeBookingStatus(status);
  return STATUS_META[key] || { label: key || "Pending", tone: "slate" };
}

function sanitizeTxnPage(raw, page, size) {
  if (Array.isArray(raw)) {
    return {
      content: raw,
      number: page,
      size,
      totalElements: raw.length,
      totalPages: 1,
    };
  }

  return {
    content: Array.isArray(raw?.content) ? raw.content : [],
    number: Number(raw?.number ?? page) || 0,
    size: Number(raw?.size ?? size) || size,
    totalElements: Number(raw?.totalElements ?? 0) || 0,
    totalPages: Number(raw?.totalPages ?? 0) || 0,
  };
}

function getBookingKey(vehicle, booking) {
  return String(
    booking?.id ??
      `${vehicle?.registrationNumber || vehicle?.id || "vehicle"}-${booking?.pickupDate || "x"}-${booking?.pickupTime || "x"}`
  );
}

function getBookingDate(booking) {
  const candidates = [
    booking?.pickupAt,
    booking?.createdAt,
    booking?.pickupDate
      ? `${booking.pickupDate}T${booking.pickupTime || "00:00"}:00`
      : null,
  ];

  for (const c of candidates) {
    const iso = toIsoDate(c);
    if (iso) return iso;
  }
  return null;
}

function getStatusCounts(rows) {
  return rows.reduce(
    (acc, row) => {
      const key = normalizeBookingStatus(row.payoutStatus);
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    },
    { PENDING: 0, QUEUED: 0, PROCESSING: 0, PAID: 0, HOLD: 0, FAILED: 0 }
  );
}

function cleanText(value) {
  return String(value || "").trim().replace(/\s+/g, " ");
}

function cleanIfsc(value) {
  return String(value || "")
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, "");
}

function cleanAccountNumber(value) {
  return String(value || "").replace(/\D+/g, "");
}

function cleanUpi(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "");
}

function buildScreenModel({ owner, vehicles, account, summary, txnPage }) {
  const txns = sanitizeTxnPage(txnPage, 0, 20).content;
  const txnByBooking = new Map();

  txns.forEach((txn) => {
    if (txn?.bookingId == null) return;
    const key = String(txn.bookingId);
    const current = txnByBooking.get(key);
    if (!current || Number(txn?.id || 0) > Number(current?.id || 0)) {
      txnByBooking.set(key, txn);
    }
  });

  const rows = (Array.isArray(vehicles) ? vehicles : []).flatMap((vehicle) => {
    const bookings = Array.isArray(vehicle?.bookings) ? vehicle.bookings : [];

    return bookings.map((booking) => {
      const txn = txnByBooking.get(String(booking?.id));
      const payoutStatus = normalizeBookingStatus(
        txn?.status ||
          booking?.ownerPayoutStatus ||
          (Number(booking?.ownerNet || 0) > 0 ? "PENDING" : "PENDING")
      );

      return {
        key: getBookingKey(vehicle, booking),
        bookingId: booking?.id ?? null,
        vehicleReg: vehicle?.registrationNumber || "—",
        vehicleLabel: `${vehicle?.brand || "Vehicle"} ${vehicle?.model || ""}`.trim(),
        pickupLocation: booking?.pickupLocation || "—",
        dropLocation:
          booking?.destination ||
          (Array.isArray(booking?.destinations)
            ? booking.destinations[booking.destinations.length - 1]
            : "—") ||
          "—",
        pickupAt: getBookingDate(booking),
        ownerGross: Number(booking?.ownerGross ?? booking?.ownerFare ?? 0) || 0,
        ownerNet: Number(booking?.ownerNet ?? booking?.ownerFare ?? 0) || 0,
        payoutStatus,
        payoutRef: txn?.providerRef || booking?.ownerPayoutRef || "",
        payoutNote: txn?.failNote || booking?.ownerPayoutNote || "",
        payoutAt: txn?.processedAt || booking?.ownerPayoutAt || null,
        payoutTxnId: txn?.id ?? null,
      };
    });
  });

  return {
    owner,
    account: account || null,
    summary: summary || {},
    rows,
    txnPage: sanitizeTxnPage(txnPage, 0, 20),
  };
}

function makeAccountForm(account) {
  return {
    method: String(account?.method || "UPI").toUpperCase() === "BANK" ? "BANK" : "UPI",
    accountHolderName: account?.accountHolderName || "",
    bankAccountNumber: "",
    confirmBankAccountNumber: "",
    ifscCode: account?.ifsc || "",
    bankName: account?.bankName || "",
    beneficiaryName: account?.beneficiaryName || account?.accountHolderName || "",
    upiId: account?.upiId || "",
    bankAccountMasked: account?.accountNumberMasked || "",
  };
}

function validateAccountForm(form, existingAccount = null) {
  const errors = {};

  if (form.method === "UPI") {
    const upi = cleanUpi(form.upiId);
    if (!upi) {
      errors.upiId = "UPI ID is required.";
    } else if (!/^[a-z0-9._-]{2,}@[a-z0-9.-]{2,}$/.test(upi)) {
      errors.upiId = "Enter a valid UPI ID.";
    }
    return errors;
  }

  const holder = cleanText(form.accountHolderName);
  const bank = cleanText(form.bankName);
  const ifsc = cleanIfsc(form.ifscCode);
  const acct = cleanAccountNumber(form.bankAccountNumber);
  const confirm = cleanAccountNumber(form.confirmBankAccountNumber);
  const hasMaskedExisting = !!String(existingAccount?.accountNumberMasked || "").trim();

  if (!holder) {
    errors.accountHolderName = "Account holder name is required.";
  }

  if (!bank) {
    errors.bankName = "Bank name is required.";
  }

  if (!ifsc) {
    errors.ifscCode = "IFSC code is required.";
  } else if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(ifsc)) {
    errors.ifscCode = "Enter a valid IFSC code. Example: HDFC0001234";
  }

  if (!acct) {
    if (!hasMaskedExisting) {
      errors.bankAccountNumber = "Account number is required.";
    }
  } else if (!/^\d{6,20}$/.test(acct)) {
    errors.bankAccountNumber = "Account number must be 6 to 20 digits.";
  }

  if (acct) {
    if (!confirm) {
      errors.confirmBankAccountNumber = "Please confirm account number.";
    } else if (acct !== confirm) {
      errors.confirmBankAccountNumber = "Account number and confirmation must match.";
    }
  }

  return errors;
}

function StatusBadge({ status }) {
  const info = statusInfo(status);
  return (
    <span
      className={`inline-flex items-center rounded-lg border px-2.5 py-1 text-[11px] font-semibold ${toneClass[info.tone]}`}
    >
      {info.label}
    </span>
  );
}

function StatCard({ title, value, hint, tone = "default" }) {
  const classes =
    tone === "emerald"
      ? "border-emerald-200 bg-white"
      : tone === "indigo"
      ? "border-indigo-200 bg-white"
      : tone === "rose"
      ? "border-rose-200 bg-white"
      : "border-slate-200 bg-white";

  const valueClass =
    tone === "emerald"
      ? "text-emerald-700"
      : tone === "indigo"
      ? "text-indigo-700"
      : tone === "rose"
      ? "text-rose-700"
      : "text-slate-950";

  return (
    <div className={`rounded-2xl border px-4 py-4 shadow-sm ${classes}`}>
      <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">
        {title}
      </div>
      <div className={`mt-2 text-2xl font-bold tracking-tight ${valueClass}`}>
        {value}
      </div>
      <div className="mt-1 text-xs text-slate-500">{hint}</div>
    </div>
  );
}

function FilterSelect({ value, onChange, children }) {
  return (
    <select
      value={value}
      onChange={onChange}
      className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-900 outline-none transition focus:border-slate-400"
    >
      {children}
    </select>
  );
}

function DetailPill({ label, value }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5">
      <div className="text-[10px] font-medium uppercase tracking-[0.16em] text-slate-500">
        {label}
      </div>
      <div className="mt-1 text-sm font-semibold text-slate-900">{value}</div>
    </div>
  );
}

function EmptyState({ text }) {
  return (
    <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-8 text-center text-sm text-slate-500">
      {text}
    </div>
  );
}

function Field({ label, error, children }) {
  return (
    <label className="grid gap-1.5 text-sm text-slate-700">
      <span className="font-medium">{label}</span>
      {children}
      {error ? <span className="text-xs text-rose-600">{error}</span> : null}
    </label>
  );
}

function LedgerCard({ row }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <div className="text-base font-semibold text-slate-950">
              Booking #{row.bookingId || "—"}
            </div>
            <StatusBadge status={row.payoutStatus} />
          </div>

          <div className="mt-2 text-sm text-slate-500">
            {formatDateTime(row.pickupAt)}
          </div>

          <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <DetailPill label="Vehicle" value={row.vehicleLabel || "Vehicle"} />
            <DetailPill label="Reg no" value={row.vehicleReg || "—"} />
            <DetailPill label="Owner net" value={money(row.ownerNet)} />
            <DetailPill label="Reference" value={row.payoutRef || "—"} />
          </div>

          <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 px-3 py-3">
            <div className="text-[10px] font-medium uppercase tracking-[0.14em] text-slate-500">
              Route
            </div>
            <div className="mt-1 text-sm text-slate-900">{row.pickupLocation || "—"}</div>
            <div className="mt-1 text-sm text-slate-500">to {row.dropLocation || "—"}</div>
          </div>

          {row.payoutNote ? (
            <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 px-3 py-3 text-sm text-rose-700">
              {row.payoutNote}
            </div>
          ) : null}
        </div>

        <div className="w-full lg:w-[180px] lg:shrink-0">
          <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-3">
            <div className="text-[10px] font-medium uppercase tracking-[0.14em] text-slate-500">
              Settlement
            </div>
            <div className="mt-2 text-sm font-semibold text-slate-900">
              {row.payoutAt ? `Updated ${formatDateTime(row.payoutAt)}` : "Not settled yet"}
            </div>
            <div className="mt-2 text-xs text-slate-500">
              Txn #{row.payoutTxnId || "—"}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function TransactionCard({ txn }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-slate-950">
            Txn #{txn.id} · Booking #{txn.bookingId || "—"}
          </div>
          <div className="mt-1 text-xs text-slate-500">
            Amount {money(txn.amount)} • {txn.provider || "—"}
          </div>
          <div className="mt-1 text-xs text-slate-500">
            Ref {txn.providerRef || "—"}
          </div>
          <div className="mt-1 text-xs text-slate-500">
            Last attempt {txn.lastAttemptAt ? formatDateTime(txn.lastAttemptAt) : "—"}
          </div>
          {txn.failNote ? (
            <div className="mt-2 text-xs text-rose-600">
              {txn.failCode ? `${txn.failCode}: ` : ""}
              {txn.failNote}
            </div>
          ) : null}
        </div>

        <StatusBadge status={txn.status} />
      </div>
    </div>
  );
}

export default function EarningsDashboard() {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");

  const [period, setPeriod] = useState("all");
  const [vehicleFilter, setVehicleFilter] = useState("all");
  const [payoutFilter, setPayoutFilter] = useState("all");

  const [owner, setOwner] = useState(null);
  const [account, setAccount] = useState(null);
  const [summary, setSummary] = useState(null);
  const [txnPage, setTxnPage] = useState({
    content: [],
    number: 0,
    size: 20,
    totalElements: 0,
    totalPages: 0,
  });
  const [vehicles, setVehicles] = useState([]);

  const [editingAccount, setEditingAccount] = useState(false);
  const [accountForm, setAccountForm] = useState(makeAccountForm(null));
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async (nextPage = 0, mode = "full") => {
    try {
      if (mode === "full") setLoading(true);
      else setRefreshing(true);

      if (mode === "full") {
        setError("");
        setToast("");
      }

      const data = await fetchOwnerPayoutScreenData({ page: nextPage, size: 20 });

      setOwner(data.owner || null);
      setVehicles(Array.isArray(data.vehicles) ? data.vehicles : []);
      setAccount(data.account || null);
      setSummary(data.summary || null);
      setTxnPage(sanitizeTxnPage(data.txnPage, nextPage, 20));
      setAccountForm(makeAccountForm(data.account));
    } catch (err) {
      setError(err?.message || "We could not load payout data right now.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load(0, "full");
  }, [load]);

  const model = useMemo(
    () => buildScreenModel({ owner, vehicles, account, summary, txnPage }),
    [owner, vehicles, account, summary, txnPage]
  );

  const vehicleOptions = useMemo(() => {
    const opts = [{ value: "all", label: "All vehicles" }];
    const seen = new Set();

    model.rows.forEach((row) => {
      const key = row.vehicleReg || row.vehicleLabel;
      if (!key || seen.has(key)) return;
      seen.add(key);
      opts.push({ value: row.vehicleReg, label: `${row.vehicleLabel} · ${row.vehicleReg}` });
    });

    return opts;
  }, [model.rows]);

  const filteredRows = useMemo(() => {
    const now = Date.now();

    const periodStart = (() => {
      if (period === "7d") return now - 7 * 24 * 60 * 60 * 1000;
      if (period === "30d") return now - 30 * 24 * 60 * 60 * 1000;
      if (period === "90d") return now - 90 * 24 * 60 * 60 * 1000;
      return null;
    })();

    return model.rows
      .filter((row) => {
        if (vehicleFilter !== "all" && row.vehicleReg !== vehicleFilter) return false;
        if (payoutFilter !== "all" && normalizeBookingStatus(row.payoutStatus) !== payoutFilter) {
          return false;
        }
        if (!periodStart) return true;
        const dt = row.pickupAt ? new Date(row.pickupAt).getTime() : null;
        if (!dt) return false;
        return dt >= periodStart && dt <= now;
      })
      .sort((a, b) => {
        const ad = a.pickupAt ? new Date(a.pickupAt).getTime() : 0;
        const bd = b.pickupAt ? new Date(b.pickupAt).getTime() : 0;
        return bd - ad;
      });
  }, [model.rows, vehicleFilter, payoutFilter, period]);

  const counts = useMemo(() => getStatusCounts(filteredRows), [filteredRows]);

  const totalEligible = filteredRows.reduce(
    (sum, row) =>
      sum + (normalizeBookingStatus(row.payoutStatus) === "PENDING" ? row.ownerNet : 0),
    0
  );

  const totalQueuedProcessing = filteredRows.reduce(
    (sum, row) =>
      sum +
      (["QUEUED", "PROCESSING"].includes(normalizeBookingStatus(row.payoutStatus))
        ? row.ownerNet
        : 0),
    0
  );

  const totalPaid = filteredRows.reduce(
    (sum, row) =>
      sum +
      (["PAID", "SUCCESS"].includes(normalizeBookingStatus(row.payoutStatus))
        ? row.ownerNet
        : 0),
    0
  );

  const totalHoldFailed = filteredRows.reduce(
    (sum, row) =>
      sum +
      (["HOLD", "FAILED"].includes(normalizeBookingStatus(row.payoutStatus))
        ? row.ownerNet
        : 0),
    0
  );

  const handleAccountField = (field, value) => {
    setAccountForm((prev) => ({ ...prev, [field]: value }));
    setFieldErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleSubmit = async () => {
    const errors = validateAccountForm(accountForm, account);
    if (Object.keys(errors).length) {
      setFieldErrors(errors);
      return;
    }

    try {
      setSubmitting(true);
      setError("");
      setToast("");

      const payload =
        accountForm.method === "UPI"
          ? {
              method: "UPI",
              upiId: cleanUpi(accountForm.upiId),
            }
          : {
              method: "BANK",
              accountHolderName: cleanText(accountForm.accountHolderName),
              bankName: cleanText(accountForm.bankName),
              ifscCode: cleanIfsc(accountForm.ifscCode),
              ...(cleanAccountNumber(accountForm.bankAccountNumber)
                ? { bankAccountNumber: cleanAccountNumber(accountForm.bankAccountNumber) }
                : {}),
            };

      const saved = await submitOwnerPayoutAccount(payload);
      setAccount(saved || null);
      setEditingAccount(false);
      setToast("Payout account submitted.");
      await load(0, "refresh");
    } catch (err) {
      setError(err?.message || "Failed to submit payout account.");
    } finally {
      setSubmitting(false);
    }
  };

  const ownerName = owner?.displayName || owner?.name || owner?.phoneNumber || "Owner";

  return (
    <div className="min-h-screen flex flex-col bg-slate-50 text-slate-900">
      <Header />

      <main className="flex-1">
        <div className="mx-auto max-w-[1560px] px-4 py-6 sm:px-6 lg:px-10 2xl:px-12">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                Fleet finance
              </p>

              <div className="mt-1 flex flex-col items-start gap-3 sm:flex-row sm:flex-wrap sm:items-center">
                <h1 className="text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">
                  Owner earnings
                </h1>
              </div>

              <p className="mt-2 text-[13px] font-medium text-slate-500">
                Earnings visibility under{" "}
                <span className="font-semibold text-slate-800">{ownerName}</span>.
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => load(0, "refresh")}
                className="inline-flex h-11 items-center justify-center rounded-xl bg-slate-900 px-5 text-sm font-semibold text-white shadow-[0_10px_28px_rgba(15,23,42,0.45)] transition-transform transition-shadow hover:translate-y-px hover:shadow-[0_8px_20px_rgba(15,23,42,0.45)]"
              >
                {refreshing ? "Refreshing…" : "Refresh"}
              </button>
            </div>
          </div>

          {error ? (
            <div className="mt-6 rounded-3xl border border-rose-200 bg-rose-50 px-4 py-4 text-sm text-rose-700">
              {error}
            </div>
          ) : null}

          {toast ? (
            <div className="mt-6 rounded-3xl border border-emerald-200 bg-emerald-50 px-4 py-4 text-sm text-emerald-700">
              {toast}
            </div>
          ) : null}

          {loading ? (
            <div className="mt-10 grid place-items-center text-sm text-slate-500">
              Loading owner earnings…
            </div>
          ) : (
            <>
              <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5">
                <StatCard
                  title="Eligible payout"
                  value={money(summary?.pendingAmount ?? totalEligible)}
                  hint={`${summary?.pendingCount ?? counts.PENDING} bookings waiting`}
                />
                <StatCard
                  title="In pipeline"
                  value={money(totalQueuedProcessing)}
                  hint={`${counts.QUEUED + counts.PROCESSING} rows in progress`}
                  tone="indigo"
                />
                <StatCard
                  title="Paid out"
                  value={money(summary?.paidAmount ?? totalPaid)}
                  hint={`${summary?.paidCount ?? counts.PAID} settlements done`}
                  tone="emerald"
                />
                <StatCard
                  title="Issues"
                  value={money(totalHoldFailed)}
                  hint={`${counts.HOLD} hold • ${counts.FAILED} failed`}
                  tone="rose"
                />
                <StatCard
                  title="Payout account"
                  value={statusInfo(account?.status || summary?.accountStatus || "DRAFT").label}
                  hint={String(account?.method || summary?.accountMethod || "UPI").toUpperCase()}
                />
              </div>

              <div className="mt-6 grid gap-6 xl:grid-cols-[1.6fr_1fr]">
                <section className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
                  <div className="border-b border-slate-100 px-4 py-4 sm:px-5">
                    <div className="flex flex-col gap-4">
                      <div>
                        <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                          Payout ledger
                        </div>
                        <h2 className="mt-1 text-lg font-bold text-slate-950">
                          Booking earnings
                        </h2>
                        <p className="mt-1 text-sm text-slate-600">
                          Clean booking-level view of owner net amount, settlement state, route, and payout reference.
                        </p>
                      </div>

                      <div className="grid gap-3 sm:grid-cols-3">
                        <FilterSelect value={period} onChange={(e) => setPeriod(e.target.value)}>
                          {PERIODS.map((p) => (
                            <option key={p.value} value={p.value}>
                              {p.label}
                            </option>
                          ))}
                        </FilterSelect>

                        <FilterSelect
                          value={vehicleFilter}
                          onChange={(e) => setVehicleFilter(e.target.value)}
                        >
                          {vehicleOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </FilterSelect>

                        <FilterSelect
                          value={payoutFilter}
                          onChange={(e) => setPayoutFilter(e.target.value)}
                        >
                          <option value="all">All payout states</option>
                          <option value="PENDING">Pending</option>
                          <option value="QUEUED">Queued</option>
                          <option value="PROCESSING">Processing</option>
                          <option value="PAID">Paid</option>
                          <option value="HOLD">Hold</option>
                          <option value="FAILED">Failed</option>
                        </FilterSelect>
                      </div>
                    </div>
                  </div>

                  <div className="p-4 sm:p-5">
                    {filteredRows.length === 0 ? (
                      <EmptyState text="No payout rows found for the current filters." />
                    ) : (
                      <div className="space-y-4">
                        {filteredRows.map((row) => (
                          <LedgerCard key={row.key} row={row} />
                        ))}
                      </div>
                    )}
                  </div>
                </section>

                <div className="space-y-6">
                  <section className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
                    <div className="border-b border-slate-100 px-4 py-4 sm:px-5">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                            Settlements
                          </div>
                          <h2 className="mt-1 text-lg font-bold text-slate-950">
                            Payout details
                          </h2>
                          <p className="mt-1 text-sm text-slate-600">
                            This destination will be used for eligible owner settlements.
                          </p>
                        </div>

                        <StatusBadge status={account?.status || summary?.accountStatus || "DRAFT"} />
                      </div>
                    </div>

                    <div className="px-4 py-4 sm:px-5">
                      {!editingAccount ? (
                        <div className="space-y-4">
                          <DetailPill
                            label="Method"
                            value={String(account?.method || summary?.accountMethod || "UPI").toUpperCase()}
                          />

                          <DetailPill
                            label="Destination"
                            value={
                              account?.upiId ||
                              account?.accountNumberMasked ||
                              "Not added yet"
                            }
                          />

                          {account?.ifsc ? (
                            <DetailPill label="IFSC" value={account.ifsc} />
                          ) : null}

                          {account?.bankName ? (
                            <DetailPill label="Bank name" value={account.bankName} />
                          ) : null}

                          {account?.accountHolderName ? (
                            <DetailPill label="Account holder" value={account.accountHolderName} />
                          ) : null}

                          {account?.issueNote ? (
                            <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-3 text-sm text-rose-700">
                              {account.issueNote}
                            </div>
                          ) : null}

                          <button
                            type="button"
                            onClick={() => {
                              setEditingAccount(true);
                              setFieldErrors({});
                              setAccountForm(makeAccountForm(account));
                              setError("");
                              setToast("");
                            }}
                            className="inline-flex h-11 w-full items-center justify-center rounded-xl bg-slate-900 px-4 text-sm font-semibold text-white shadow-[0_10px_28px_rgba(15,23,42,0.45)] hover:bg-black"
                          >
                            {account ? "Edit payout details" : "Add payout details"}
                          </button>
                        </div>
                      ) : (
                        <div className="space-y-4 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                          <Field label="Payout method">
                            <div className="grid grid-cols-2 gap-3">
                              <button
                                type="button"
                                onClick={() => handleAccountField("method", "UPI")}
                                className={`h-11 rounded-xl border text-sm font-semibold ${
                                  accountForm.method === "UPI"
                                    ? "border-slate-900 bg-slate-900 text-white"
                                    : "border-slate-200 bg-white text-slate-800"
                                }`}
                              >
                                UPI
                              </button>

                              <button
                                type="button"
                                onClick={() => handleAccountField("method", "BANK")}
                                className={`h-11 rounded-xl border text-sm font-semibold ${
                                  accountForm.method === "BANK"
                                    ? "border-slate-900 bg-slate-900 text-white"
                                    : "border-slate-200 bg-white text-slate-800"
                                }`}
                              >
                                Bank
                              </button>
                            </div>
                          </Field>

                          {accountForm.method === "UPI" ? (
                            <Field label="UPI ID" error={fieldErrors.upiId}>
                              <input
                                value={accountForm.upiId}
                                onChange={(e) => handleAccountField("upiId", e.target.value)}
                                placeholder="name@bank"
                                className="h-11 w-full rounded-xl border border-slate-200 px-3 text-sm outline-none focus:border-slate-400"
                              />
                            </Field>
                          ) : (
                            <>
                              <Field label="Account holder name" error={fieldErrors.accountHolderName}>
                                <input
                                  value={accountForm.accountHolderName}
                                  onChange={(e) => handleAccountField("accountHolderName", e.target.value)}
                                  className="h-11 w-full rounded-xl border border-slate-200 px-3 text-sm outline-none focus:border-slate-400"
                                />
                              </Field>

                              <Field label="Bank account number" error={fieldErrors.bankAccountNumber}>
                                <input
                                  value={accountForm.bankAccountNumber}
                                  onChange={(e) =>
                                    handleAccountField(
                                      "bankAccountNumber",
                                      e.target.value.replace(/\D+/g, "")
                                    )
                                  }
                                  inputMode="numeric"
                                  className="h-11 w-full rounded-xl border border-slate-200 px-3 text-sm outline-none focus:border-slate-400"
                                />
                              </Field>

                              <Field
                                label="Confirm bank account number"
                                error={fieldErrors.confirmBankAccountNumber}
                              >
                                <input
                                  value={accountForm.confirmBankAccountNumber}
                                  onChange={(e) =>
                                    handleAccountField(
                                      "confirmBankAccountNumber",
                                      e.target.value.replace(/\D+/g, "")
                                    )
                                  }
                                  inputMode="numeric"
                                  className="h-11 w-full rounded-xl border border-slate-200 px-3 text-sm outline-none focus:border-slate-400"
                                />
                              </Field>

                              <Field label="IFSC code" error={fieldErrors.ifscCode}>
                                <input
                                  value={accountForm.ifscCode}
                                  onChange={(e) =>
                                    handleAccountField(
                                      "ifscCode",
                                      e.target.value.toUpperCase().replace(/[\s-]+/g, "")
                                    )
                                  }
                                  className="h-11 w-full rounded-xl border border-slate-200 px-3 text-sm uppercase outline-none focus:border-slate-400"
                                />
                              </Field>

                              <Field label="Bank name" error={fieldErrors.bankName}>
                                <input
                                  value={accountForm.bankName}
                                  onChange={(e) => handleAccountField("bankName", e.target.value)}
                                  className="h-11 w-full rounded-xl border border-slate-200 px-3 text-sm outline-none focus:border-slate-400"
                                />
                              </Field>

                              {accountForm.bankAccountMasked ? (
                                <div className="text-xs text-slate-500">
                                  Saved account on file: {accountForm.bankAccountMasked}
                                </div>
                              ) : null}
                            </>
                          )}

                          <div className="flex flex-col gap-3 sm:flex-row">
                            <button
                              type="button"
                              onClick={() => {
                                setEditingAccount(false);
                                setFieldErrors({});
                                setAccountForm(makeAccountForm(account));
                                setError("");
                                setToast("");
                              }}
                              className="inline-flex h-11 items-center justify-center rounded-xl border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-700 hover:border-slate-900 hover:bg-slate-900 hover:text-white"
                            >
                              Cancel
                            </button>

                            <button
                              type="button"
                              disabled={submitting}
                              onClick={handleSubmit}
                              className="inline-flex h-11 flex-1 items-center justify-center rounded-xl bg-slate-900 px-4 text-sm font-semibold text-white shadow-[0_10px_28px_rgba(15,23,42,0.45)] hover:bg-black disabled:opacity-60"
                            >
                              {submitting ? "Submitting…" : "Submit"}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </section>

                  <section className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
                    <div className="border-b border-slate-100 px-4 py-4 sm:px-5">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                            Transfers
                          </div>
                          <h2 className="mt-1 text-lg font-bold text-slate-950">
                            Recent payout transactions
                          </h2>
                          <p className="mt-1 text-sm text-slate-600">
                            Latest processor activity and transfer results.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="px-4 py-4 sm:px-5">
                      <div className="grid grid-cols-2 gap-3">
                        <DetailPill label="Queued" value={String(counts.QUEUED)} />
                        <DetailPill label="Processing" value={String(counts.PROCESSING)} />
                        <DetailPill label="Paid" value={String(counts.PAID)} />
                        <DetailPill label="Hold / failed" value={`${counts.HOLD} / ${counts.FAILED}`} />
                      </div>

                      <div className="mt-4 space-y-3">
                        {txnPage.content.length === 0 ? (
                          <EmptyState text="No payout transactions available yet." />
                        ) : (
                          txnPage.content.slice(0, 8).map((txn) => (
                            <TransactionCard key={txn.id} txn={txn} />
                          ))
                        )}
                      </div>
                    </div>
                  </section>
                </div>
              </div>
            </>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}